import * as React from 'react';
import {
    defaultPasskeyContext,
    PasskeyContext,
    PasskeyRegistrationState,
    WalletPasskeyContext,
} from '../PasskeyContext';
import { PASSKEY_PREFERENCE_KEY } from '../passkeyPreference';
import { useServiceResolver } from '@services/useServiceResolver';
import { useAppContext } from '@contexts/app/hooks/useAppContext';
import { ResponseCode } from '@ul/api';
import { PasskeyWeb } from '@ul/common';
import { usePushNotification } from '@components/Notification/useNotification';
import { useTranslation } from '@contexts/translation/hooks/useTranslation';
import { LoginFormCmsWithDefault } from '@components/forms/login/LoginForm/LoginFormCms';
import { RegisterPasskeyFormCmsWithDefault } from '@v2/components/Forms/RegisterPasskeyForm/RegisterPasskeyFormCmsWithDefault';
import type { RegistrationResponseJSON, AuthenticationResponseJSON } from '@simplewebauthn/browser';
import type {
    PublicKeyCredentialCreationOptionsJSON,
    PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

import {
    PasskeyRegistrationPlatformResponse,
    PasskeyAuthenticationPlatformResponse,
    PasskeyRegistrationExitState,
} from '@ul/common';
import { useIsMobileApp } from '@contexts/app/hooks/useIsMobileApp';
import { useAppSetting } from '@contexts/appSetting/hooks/useAppSetting';
import { useAnalytics } from './analytics';
import {
    PasskeyEventName,
    PasskeyOperation,
    PasskeyTriggerSource,
    PasskeyErrorCategory,
} from '@services/hadoopMessage/Messenger';

const USER_CANCELLED_REGISTRATION = 'User cancelled the registration';
const USER_CANCELLED_AUTH = 'User cancelled the authentication';
const AUTH_ABORTED = 'Authentication was cancelled';

const METRIC_PASSKEY = 'ul.clientside.passkey';

export const METRIC_TYPES = {
    PLATFORM_AUTH_REQUEST: 'platform_authenticate_request',
    PLATFORM_AUTH_RESPONSE: 'platform_authenticate_response',
    UL_AUTH_RESPONSE: 'ul_authenticate_response',
    UL_REGISTER_RESPONSE: 'ul_register_response',
    PLATFORM_REGISTER_REQUEST: 'platform_register_request',
    PLATFORM_REGISTER_RESPONSE: 'platform_register_response',
    PASSKEY_REGISTER_EXIT: 'passkey_registration_exit',
} as const;

const formatAuthNote = (useAutofill: boolean, isInputEmailFlow: boolean): string => {
    return 'autofill=' + useAutofill.toString() + ' isInputEmailFlow=' + isInputEmailFlow.toString();
};

const formatResponseCode = (responseCode: ResponseCode): string => {
    return (ResponseCode as any)[responseCode] ?? String(responseCode);
};

export function withPasskeyContext<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
    const ComponentWithPasskeyContext: React.FC<TProps> = props => {
        const { loginService, sdkEffect, measurement, logger, messenger } = useServiceResolver();
        const { passkeySupported, iframeMode, appId, userId } = useAppContext();
        const isMobileApp = useIsMobileApp();
        const { feature } = useAppSetting();
        const host = window.location.host;

        const pushNotification = usePushNotification('RegisterPasskey');
        const pushUnifiedAuthNotification = usePushNotification('UnifiedAuth');
        const legacyFormTranslation = useTranslation(LoginFormCmsWithDefault);
        const translation = useTranslation(RegisterPasskeyFormCmsWithDefault);
        const {
            AnalyticsPasskeyTechnicalErrorNotificationProps,
            AnalyticsPasskeyCreatedNotificationProps,
        } = useAnalytics();

        const [registrationState, setRegistrationState] = React.useState<PasskeyRegistrationState>('idle');
        const [isLoading, setIsLoading] = React.useState(false);
        const [isPasskeyAutofillSupported, setIsPasskeyAutofillSupported] = React.useState(false);
        const [error, setError] = React.useState<string | null>(null);
        const [registrationTriggerSource, setRegistrationTriggerSource] = React.useState<PasskeyTriggerSource>(
            PasskeyTriggerSource.LOGIN
        );

        // Upstream can enable passkeys for rollout/host support; on web we additionally require actual WebAuthn support.
        const isSupported = !!passkeySupported && (isMobileApp ? true : PasskeyWeb.isSupported());

        const measurementTags = {
            appId: appId,
            origin: origin,
        };

        const sendRegistrationError = React.useCallback(
            (eventName: PasskeyEventName, error: unknown, note?: string) => {
                const errorDescription = error instanceof Error ? error.message : String(error);
                messenger.sendPasskeyEvent({
                    operation: PasskeyOperation.REGISTRATION,
                    event_name: eventName,
                    trigger_source: registrationTriggerSource,
                    error_category: PasskeyErrorCategory.SYSTEM_ERROR,
                    error_description: errorDescription,
                    metadata: JSON.stringify(error),
                    ...(note && { note }),
                });
            },
            [messenger, registrationTriggerSource]
        );

        const sendAuthenticationError = React.useCallback(
            (eventName: PasskeyEventName, error: unknown, note?: string) => {
                const errorDescription = error instanceof Error ? error.message : String(error);
                messenger.sendPasskeyEvent({
                    operation: PasskeyOperation.AUTHENTICATION,
                    event_name: eventName,
                    trigger_source: PasskeyTriggerSource.LOGIN,
                    error_category: PasskeyErrorCategory.SYSTEM_ERROR,
                    error_description: errorDescription,
                    metadata: JSON.stringify(error),
                    ...(note && { note }),
                });
            },
            [messenger]
        );

        // Get passkey registration options from UL-API
        const getPasskeyRegistrationOptions = React.useCallback(async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await loginService.getPasskeyRegistrationOptions();

                return response;
            } catch (err) {
                sendRegistrationError(
                    PasskeyEventName.REGISTRATION_ULSDK_RESPONSE_ERROR,
                    err,
                    'registration_fetch_options_unsuccessful'
                );
                setError('Network error while getting registration options');
                throw err;
            } finally {
                setIsLoading(false);
            }
        }, [loginService]);

        // Request passkey registration on platform (browser WebAuthn)
        const requestRegistrationOnPlatform = React.useCallback(
            async (options: PublicKeyCredentialCreationOptionsJSON): Promise<PasskeyRegistrationPlatformResponse> => {
                try {
                    measurement.count(METRIC_PASSKEY, 1, {
                        ...measurementTags,
                        type: METRIC_TYPES.PLATFORM_REGISTER_REQUEST,
                    });
                    setIsLoading(true);
                    setRegistrationState('platform_register');

                    // Call SDK effect to communicate with parent/platform
                    const platformResponse = await sdkEffect.onPasskeyRegister(options);

                    return platformResponse;
                } catch (err) {
                    if (!isMobileApp) {
                        sendRegistrationError(PasskeyEventName.REGISTRATION_LOCAL_ERROR, err);
                    }
                    setError('Platform registration failed');
                    setRegistrationState('error');
                    setIsLoading(false);
                    pushNotification({
                        color: 'error',
                        message: legacyFormTranslation.passkeyRegistrationFailed,
                        testId: 'passkey-registration-failed',
                        analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                    });
                    throw err;
                }
            },
            [
                sdkEffect,
                legacyFormTranslation,
                pushNotification,
                AnalyticsPasskeyTechnicalErrorNotificationProps,
                measurement,
                measurementTags,
            ]
        );

        const onPasskeyRegistrationExit = React.useCallback(
            (exitState: PasskeyRegistrationExitState) => {
                if (!isMobileApp) {
                    switch (exitState) {
                        case PasskeyRegistrationExitState.PASSKEY_NOT_NOW:
                            messenger.sendPasskeyEvent({
                                operation: PasskeyOperation.REGISTRATION,
                                event_name: PasskeyEventName.REGISTRATION_LOCAL_NOT_NOW,
                                trigger_source: registrationTriggerSource,
                            });

                            break;
                        case PasskeyRegistrationExitState.PASSKEY_BACK:
                            messenger.sendPasskeyEvent({
                                operation: PasskeyOperation.REGISTRATION,
                                event_name: PasskeyEventName.REGISTRATION_LOCAL_BACK,
                                trigger_source: registrationTriggerSource,
                            });
                            break;
                    }
                }

                measurement.count(METRIC_PASSKEY, 1, {
                    ...measurementTags,
                    type: METRIC_TYPES.PASSKEY_REGISTER_EXIT,
                    state: exitState.toString(),
                });
                sdkEffect.onPasskeyRegistrationExit(exitState);
            },
            [isMobileApp, messenger, measurement, measurementTags, registrationTriggerSource, sdkEffect]
        );

        // Complete registration with UL-API
        const registerPasskey = React.useCallback(
            async (
                platformResponse: RegistrationResponseJSON,
                goToVerifyOtpCallback?: () => void,
                goToPasskeyManagement?: () => void
            ) => {
                try {
                    setIsLoading(true);
                    setRegistrationState('completing');

                    const response = await loginService.registerPasskey({
                        responseJson: JSON.stringify(platformResponse),
                    });

                    measurement.count(METRIC_PASSKEY, 1, {
                        ...measurementTags,
                        type: METRIC_TYPES.UL_REGISTER_RESPONSE,
                        source: registrationTriggerSource,
                        success: (response.responseCode === ResponseCode.Successful).toString(),
                        responseCode: formatResponseCode(response.responseCode),
                    });

                    if (response.responseCode === ResponseCode.Successful) {
                        setRegistrationState('success');
                        pushNotification({
                            color: 'success',
                            message: translation.RegisterPasskeySuccess,
                            testId: 'passkey-registration-success',
                            analyticsProps: AnalyticsPasskeyCreatedNotificationProps,
                        });

                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.REGISTRATION,
                            event_name: PasskeyEventName.REGISTRATION_ULSDK_RESPONSE_SUCCESS,
                            trigger_source: registrationTriggerSource,
                            response_code: formatResponseCode(response.responseCode),
                        });

                        setTimeout(() => {
                            if (registrationTriggerSource === PasskeyTriggerSource.SETTINGS_FLOW) {
                                setRegistrationTriggerSource(PasskeyTriggerSource.LOGIN);
                                goToPasskeyManagement();
                            } else {
                                onPasskeyRegistrationExit(PasskeyRegistrationExitState.PASSKEY_CREATED);
                            }
                        }, 2000);
                    } else if (response.responseCode === ResponseCode.NeedOtp) {
                        setRegistrationState('need_otp');
                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.REGISTRATION,
                            event_name: PasskeyEventName.REGISTRATION_ULSDK_RESPONSE_ERROR,
                            trigger_source: registrationTriggerSource,
                            error_category: PasskeyErrorCategory.SYSTEM_ERROR,
                            error_description: 'registration_final_need_otp',
                            response_code: formatResponseCode(response.responseCode),
                        });
                        if (goToVerifyOtpCallback) {
                            goToVerifyOtpCallback();
                        }
                    } else {
                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.REGISTRATION,
                            event_name: PasskeyEventName.REGISTRATION_ULSDK_RESPONSE_ERROR,
                            trigger_source: registrationTriggerSource,
                            error_category: PasskeyErrorCategory.SYSTEM_ERROR,
                            response_code: formatResponseCode(response.responseCode),
                        });
                        setError('Failed to complete passkey registration');
                        setRegistrationState('error');
                        pushNotification({
                            color: 'error',
                            message: legacyFormTranslation.passkeyRegistrationFailed,
                            testId: 'passkey-registration-failed',
                            analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                        });
                    }
                } catch (err) {
                    sendRegistrationError(PasskeyEventName.REGISTRATION_ULSDK_RESPONSE_ERROR, err);
                    setError('Network error during completion');
                    setRegistrationState('error');
                    pushNotification({
                        color: 'error',
                        message: legacyFormTranslation.passkeyRegistrationFailed,
                        testId: 'passkey-registration-failed',
                        analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                    });
                } finally {
                    setIsLoading(false);
                }
            },
            [
                loginService,
                legacyFormTranslation,
                messenger,
                pushNotification,
                translation,
                onPasskeyRegistrationExit,
                AnalyticsPasskeyTechnicalErrorNotificationProps,
                AnalyticsPasskeyCreatedNotificationProps,
                measurement,
                measurementTags,
                registrationTriggerSource,
                sendRegistrationError,
            ]
        );

        // Perform full passkey registration flow
        const performPasskeyRegistration = React.useCallback(
            async (goToVerifyOtpCallback?: () => void, goToPasskeyManagement?: () => void) => {
                try {
                    setIsLoading(true);
                    setError(null);
                    setRegistrationState('initiating');

                    if (!isMobileApp) {
                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.REGISTRATION,
                            event_name: PasskeyEventName.REGISTRATION_LOCAL_INIT,
                            trigger_source: registrationTriggerSource,
                        });
                    }

                    // Step 1: Get registration options from server
                    const response = await getPasskeyRegistrationOptions();

                    // Check if OTP is needed
                    if (response.responseCode === ResponseCode.NeedOtp) {
                        setRegistrationState('need_otp');
                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.REGISTRATION,
                            event_name: PasskeyEventName.REGISTRATION_ULSDK_RESPONSE_ERROR,
                            trigger_source: registrationTriggerSource,
                            error_category: PasskeyErrorCategory.SYSTEM_ERROR,
                            error_description: 'registration_options_need_otp',
                            response_code: formatResponseCode(response.responseCode),
                        });
                        // Use callback if provided, otherwise fall back to context's goToVerifyOtp
                        if (goToVerifyOtpCallback) {
                            goToVerifyOtpCallback();
                        }
                        return response;
                    }

                    if (!response.success || response.responseCode !== ResponseCode.Successful) {
                        setError('Failed to initiate passkey registration');
                        setRegistrationState('error');
                        pushNotification({
                            color: 'error',
                            message: legacyFormTranslation.passkeyRegistrationFailed,
                            testId: 'passkey-registration-failed',
                            analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                        });
                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.REGISTRATION,
                            event_name: PasskeyEventName.REGISTRATION_ULSDK_RESPONSE_ERROR,
                            trigger_source: registrationTriggerSource,
                            error_category: PasskeyErrorCategory.SYSTEM_ERROR,
                            error_description: 'registration_fetch_options_unsuccessful',
                            note: 'registration_fetch_options_unsuccessful',
                            response_code: formatResponseCode(response.responseCode),
                        });
                        return response;
                    }

                    if (response.success && response.options) {
                        // Step 2: Register on platform (shows native prompt, returns credential)
                        const platformResponse = await requestRegistrationOnPlatform(response.options);

                        const registerTags: Record<string, string> = {
                            ...measurementTags,
                            type: METRIC_TYPES.PLATFORM_REGISTER_RESPONSE,
                            success: platformResponse.success.toString(),
                        };
                        if (!platformResponse.success) {
                            logger.error(
                                'Passkey registration error platform response: ' +
                                    JSON.stringify(platformResponse.error)
                            );
                            let errorTag: string;
                            switch (platformResponse.error) {
                                case USER_CANCELLED_REGISTRATION:
                                    errorTag = 'cancelled';
                                    break;
                                default:
                                    errorTag = 'error';
                            }
                            registerTags.error = errorTag;
                        }
                        measurement.count(METRIC_PASSKEY, 1, registerTags);

                        if (!platformResponse.success || !platformResponse.responseJSON) {
                            if (platformResponse.error === USER_CANCELLED_REGISTRATION) {
                                setRegistrationState('idle');
                                setIsLoading(false);
                                if (!isMobileApp) {
                                    messenger.sendPasskeyEvent({
                                        operation: PasskeyOperation.REGISTRATION,
                                        event_name: PasskeyEventName.REGISTRATION_LOCAL_CANCEL,
                                        trigger_source: registrationTriggerSource,
                                        error_category: PasskeyErrorCategory.USER_ACTION,
                                        error_description: platformResponse.error,
                                        metadata: JSON.stringify(platformResponse),
                                    });
                                }
                                return response;
                            }

                            throw new Error(platformResponse.error || 'Failed to get credential from platform');
                        }

                        if (!isMobileApp) {
                            messenger.sendPasskeyEvent({
                                operation: PasskeyOperation.REGISTRATION,
                                event_name: PasskeyEventName.REGISTRATION_LOCAL_SUCCESS,
                                trigger_source: registrationTriggerSource,
                            });
                        }

                        // Step 3: Complete registration with server - pass the callback through
                        await registerPasskey(
                            platformResponse.responseJSON,
                            goToVerifyOtpCallback,
                            goToPasskeyManagement
                        );
                    }

                    return response;
                } catch (err) {
                    logger.error('Passkey registration flow error', err);
                    setError('Network error during registration');
                    setRegistrationState('error');
                    setIsLoading(false);
                    pushNotification({
                        color: 'error',
                        message: legacyFormTranslation.passkeyRegistrationFailed,
                        testId: 'passkey-registration-failed',
                        analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                    });
                    throw err;
                }
            },
            [
                getPasskeyRegistrationOptions,
                requestRegistrationOnPlatform,
                registerPasskey,
                legacyFormTranslation,
                pushNotification,
                AnalyticsPasskeyTechnicalErrorNotificationProps,
                measurement,
                measurementTags,
                registrationTriggerSource,
            ]
        );

        // Get passkey authentication options from UL-API
        const getPasskeyAuthenticationOptions = React.useCallback(async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await loginService.getPasskeyAuthenticationOptions();

                return response;
            } catch (err) {
                sendAuthenticationError(
                    PasskeyEventName.AUTHENTICATION_ULSDK_RESPONSE_ERROR,
                    err,
                    'authentication_fetch_options_unsuccessful'
                );
                setError('Network error during authentication initiation');
                throw err;
            } finally {
                setIsLoading(false);
            }
        }, [loginService]);

        // Complete authentication with UL-API
        const authenticateWithPasskey = React.useCallback(
            async (platformResponse: AuthenticationResponseJSON, walletContext?: WalletPasskeyContext) => {
                try {
                    setIsLoading(true);
                    setError(null);

                    const response = await loginService.authenticateWithPasskey({
                        responseJson: JSON.stringify(platformResponse),
                        ...(walletContext?.mfaOperationCode && { mfaOperationCode: walletContext.mfaOperationCode }),
                        ...(walletContext?.transactionId && { transactionId: walletContext.transactionId }),
                    });

                    if (response.responseCode !== ResponseCode.Successful) {
                        setError('Authentication failed');
                    }

                    return response;
                } catch (err) {
                    sendAuthenticationError(PasskeyEventName.AUTHENTICATION_ULSDK_RESPONSE_ERROR, err);
                    setError('Network error during authentication completion');
                    throw err;
                } finally {
                    setIsLoading(false);
                }
            },
            [loginService]
        );

        // Request authentication on platform (browser/native)
        // Returns the platform response for the component to handle
        const requestAuthenticationOnPlatform = React.useCallback(
            async (
                options: PublicKeyCredentialRequestOptionsJSON,
                useConditionalMediation: boolean = false
            ): Promise<PasskeyAuthenticationPlatformResponse> => {
                try {
                    measurement.count(METRIC_PASSKEY, 1, {
                        ...measurementTags,
                        type: METRIC_TYPES.PLATFORM_AUTH_REQUEST,
                        conditional: useConditionalMediation ? 'true' : 'false',
                    });
                    let platformResponse;
                    if (iframeMode && !isMobileApp && useConditionalMediation) {
                        platformResponse = await PasskeyWeb.startAuthentication(options, useConditionalMediation);
                    } else {
                        platformResponse = await sdkEffect.onPasskeyAuthenticate(options, useConditionalMediation);
                    }

                    return platformResponse;
                } catch (err) {
                    if (!isMobileApp) {
                        sendAuthenticationError(PasskeyEventName.AUTHENTICATION_LOCAL_ERROR, err);
                    }

                    throw err;
                }
            },
            [sdkEffect, measurement, measurementTags]
        );

        // Reset registration state
        const resetRegistration = React.useCallback(() => {
            setRegistrationState('idle');
            setIsLoading(false);
            setError(null);
        }, []);

        const handleBackButtonClick = React.useCallback(
            async (goToPasskeyManagement?: () => void) => {
                if (registrationTriggerSource === PasskeyTriggerSource.SETTINGS_FLOW && goToPasskeyManagement) {
                    goToPasskeyManagement();
                    return;
                }
                onPasskeyRegistrationExit(PasskeyRegistrationExitState.PASSKEY_BACK);
            },
            [onPasskeyRegistrationExit, registrationTriggerSource]
        );

        // Check if conditional UI (autofill) is supported on platform
        const checkAutofillSupportOnPlatform = React.useCallback(async (): Promise<boolean> => {
            try {
                if (isMobileApp) {
                    const isSupported = await sdkEffect.onPasskeySupportsAutofill();
                    return isSupported;
                }
                const isSupported = await PasskeyWeb.supportsAutofill();
                return isSupported;
            } catch (error) {
                return false;
            }
        }, [sdkEffect, isMobileApp]);

        React.useEffect(() => {
            if (feature?.passkeyAuthentication) {
                checkAutofillSupportOnPlatform().then(isSupported => {
                    setIsPasskeyAutofillSupported(isSupported);
                });
            }
        }, [checkAutofillSupportOnPlatform, feature?.passkeyAuthentication]);

        const abortPasskeyRequestOnPlatform = React.useCallback(() => {
            if (iframeMode) {
                PasskeyWeb.abortPasskeyRequest();
            }
        }, [iframeMode]);

        const performPasskeyAuthentication = React.useCallback(
            async (
                onSuccess: (responseCode: ResponseCode) => void,
                useAutofill: boolean = false,
                isInputEmailFlow: boolean = false,
                onCancelCallback: (checkPasskey: boolean) => void,
                walletContext?: WalletPasskeyContext
            ) => {
                try {
                    if (!isMobileApp) {
                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.AUTHENTICATION,
                            event_name: PasskeyEventName.AUTHENTICATION_LOCAL_INIT,
                            trigger_source: PasskeyTriggerSource.LOGIN,
                            note: formatAuthNote(useAutofill, isInputEmailFlow),
                        });
                    }

                    // Step 1: Get authentication options from server
                    const authResponse = await getPasskeyAuthenticationOptions();

                    if (!authResponse.options) {
                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.AUTHENTICATION,
                            event_name: PasskeyEventName.AUTHENTICATION_ULSDK_RESPONSE_ERROR,
                            trigger_source: PasskeyTriggerSource.LOGIN,
                            error_category: PasskeyErrorCategory.SYSTEM_ERROR,
                            error_description: 'authentication_fetch_options_unsuccessful',
                            note: formatAuthNote(useAutofill, isInputEmailFlow),
                        });
                        return;
                    }

                    // Step 2: Authenticate on platform (shows native prompt, returns credential)
                    const platformResponse = await requestAuthenticationOnPlatform(authResponse.options, useAutofill);

                    // For autofill, check success before proceeding
                    const authTags: Record<string, string> = {
                        ...measurementTags,
                        type: METRIC_TYPES.PLATFORM_AUTH_RESPONSE,
                        conditional: useAutofill ? 'true' : 'false',
                        success: platformResponse.success.toString(),
                    };
                    if (!platformResponse.success) {
                        if (isInputEmailFlow) {
                            if (onCancelCallback) onCancelCallback(false);
                        }
                        logger.error(
                            `Passkey authentication error platform response: ${JSON.stringify(
                                platformResponse?.error
                            )}`,
                            undefined,
                            { host }
                        );
                        let errorTag: string;
                        let passkeyEventName: PasskeyEventName;
                        let errorCategory: PasskeyErrorCategory;
                        if (!isMobileApp) {
                            switch (platformResponse.error) {
                                case USER_CANCELLED_AUTH:
                                    errorTag = 'cancelled';
                                    errorCategory = PasskeyErrorCategory.USER_ACTION;
                                    passkeyEventName = PasskeyEventName.AUTHENTICATION_LOCAL_USER_CANCEL;
                                    break;
                                case AUTH_ABORTED:
                                    errorTag = 'aborted';
                                    errorCategory = PasskeyErrorCategory.USER_ACTION;
                                    passkeyEventName = PasskeyEventName.AUTHENTICATION_LOCAL_ABORT;
                                    break;
                                default:
                                    errorTag = 'error';
                                    errorCategory = PasskeyErrorCategory.SYSTEM_ERROR;
                                    passkeyEventName = PasskeyEventName.AUTHENTICATION_LOCAL_ERROR;
                            }
                            messenger.sendPasskeyEvent({
                                operation: PasskeyOperation.AUTHENTICATION,
                                event_name: passkeyEventName,
                                trigger_source: PasskeyTriggerSource.LOGIN,
                                error_category: errorCategory,
                                error_description: platformResponse.error,
                                metadata: JSON.stringify(platformResponse),
                                note: formatAuthNote(useAutofill, isInputEmailFlow),
                            });
                        }

                        authTags.error = errorTag;
                    }
                    measurement.count(METRIC_PASSKEY, 1, authTags);

                    if (!platformResponse.success) {
                        // For background autofill attempts, stay silent to avoid noisy UX.
                        // For explicit user action, show a generic fallback message.
                        // shouldn't show error in case user cancel. passkey prompt from input email flow.
                        if (
                            !useAutofill &&
                            !isInputEmailFlow &&
                            platformResponse.error !== USER_CANCELLED_AUTH &&
                            platformResponse.error !== AUTH_ABORTED
                        ) {
                            pushUnifiedAuthNotification({
                                color: 'error',
                                message: legacyFormTranslation.passkeyAuthenticationFailed,
                                testId: 'passkey-authentication-failed',
                            });
                        }
                        return;
                    }

                    if (!platformResponse.responseJSON) {
                        throw new Error(platformResponse.error || 'Failed to get credential from platform');
                    }

                    if (!isMobileApp) {
                        messenger.sendPasskeyEvent({
                            operation: PasskeyOperation.AUTHENTICATION,
                            event_name: PasskeyEventName.AUTHENTICATION_LOCAL_SUCCESS,
                            trigger_source: PasskeyTriggerSource.LOGIN,
                            note: formatAuthNote(useAutofill, isInputEmailFlow),
                        });
                    }

                    // Step 3: Complete authentication with server
                    const completeResponse = await authenticateWithPasskey(
                        platformResponse.responseJSON,
                        walletContext
                    );

                    measurement.count(METRIC_PASSKEY, 1, {
                        ...measurementTags,
                        type: METRIC_TYPES.UL_AUTH_RESPONSE,
                        success: (completeResponse.responseCode === ResponseCode.Successful).toString(),
                        responseCode: formatResponseCode(completeResponse.responseCode),
                    });

                    const passkeyEventName =
                        completeResponse.responseCode === ResponseCode.Successful
                            ? PasskeyEventName.AUTHENTICATION_ULSDK_RESPONSE_SUCCESS
                            : PasskeyEventName.AUTHENTICATION_ULSDK_RESPONSE_ERROR;
                    messenger.sendPasskeyEvent({
                        operation: PasskeyOperation.AUTHENTICATION,
                        event_name: passkeyEventName,
                        trigger_source: PasskeyTriggerSource.LOGIN,
                        response_code: formatResponseCode(completeResponse.responseCode),
                        note: formatAuthNote(useAutofill, isInputEmailFlow),
                    });

                    onSuccess(completeResponse.responseCode);
                } catch (error) {
                    logger.error('Passkey authentication flow error', error);
                    // silently ignore AbortError (user likely clicked elsewhere)
                    if (error?.name === 'AbortError') {
                        return;
                    }

                    // Only rethrow for regular authentication
                    if (!useAutofill) {
                        pushUnifiedAuthNotification({
                            color: 'error',
                            message: legacyFormTranslation.passkeyAuthenticationFailed,
                            testId: 'passkey-authentication-failed',
                        });
                        throw error;
                    }
                }
            },
            [
                getPasskeyAuthenticationOptions,
                requestAuthenticationOnPlatform,
                authenticateWithPasskey,
                legacyFormTranslation,
                pushUnifiedAuthNotification,
                measurement,
                measurementTags,
            ]
        );

        const updateLocalPasskeyPreference = (skipPasskey?: boolean) => {
            localStorage.setItem(PASSKEY_PREFERENCE_KEY, JSON.stringify({ skipPasskey: !!skipPasskey }));
        };

        const contextValue: PasskeyContext = React.useMemo(
            () => ({
                registrationState,
                isLoading,
                error,
                isSupported,
                isPasskeyAutofillSupported,
                registrationTriggerSource,
                getPasskeyRegistrationOptions,
                performPasskeyRegistration,
                getPasskeyAuthenticationOptions,
                requestAuthenticationOnPlatform,
                authenticateWithPasskey,
                requestRegistrationOnPlatform,
                registerPasskey,
                resetRegistration,
                setRegistrationState,
                handleBackButtonClick,
                checkAutofillSupportOnPlatform,
                onPasskeyRegistrationExit,
                abortPasskeyRequestOnPlatform,
                performPasskeyAuthentication,
                updateLocalPasskeyPreference,
                setRegistrationTriggerSource,
            }),
            [
                registrationState,
                isLoading,
                registrationTriggerSource,
                error,
                isSupported,
                isPasskeyAutofillSupported,
                getPasskeyRegistrationOptions,
                performPasskeyRegistration,
                getPasskeyAuthenticationOptions,
                requestAuthenticationOnPlatform,
                authenticateWithPasskey,
                requestRegistrationOnPlatform,
                registerPasskey,
                resetRegistration,
                handleBackButtonClick,
                checkAutofillSupportOnPlatform,
                onPasskeyRegistrationExit,
                abortPasskeyRequestOnPlatform,
                performPasskeyAuthentication,
                updateLocalPasskeyPreference,
                setRegistrationTriggerSource,
            ]
        );

        return (
            <PasskeyContext.Provider value={contextValue}>
                <Component {...props} />
            </PasskeyContext.Provider>
        );
    };

    ComponentWithPasskeyContext.displayName = `${Component.displayName || 'Component'}WithPasskeyContext`;

    return ComponentWithPasskeyContext;
}
